import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navigation } from '@/components/layout/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Store,
  Plus,
  Loader2,
  QrCode,
  Printer,
  Copy,
  Palette,
  AlertTriangle,
  Check,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Main 14 allergens
const MAIN_ALLERGENS = [
  'Celery', 'Gluten', 'Crustaceans', 'Eggs', 'Fish', 'Lupin', 'Milk',
  'Molluscs', 'Mustard', 'Nuts', 'Peanuts', 'Sesame', 'Soy', 'Sulphites'
];

const FestivalManage = () => {
  const { festivalId } = useParams();
  const { token } = useAuth();
  const [festival, setFestival] = useState(null);
  const [wristbands, setWristbands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Wristband creation
  const [wristbandDialogOpen, setWristbandDialogOpen] = useState(false);
  const [creatingWristband, setCreatingWristband] = useState(false);
  const [newWristband, setNewWristband] = useState({
    attendee_name: '',
    attendee_email: '',
    allergens: [],
    custom_allergens: [],
    notes: ''
  });
  const [customAllergen, setCustomAllergen] = useState('');
  
  // Vendor creation
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: '',
    description: '',
    food_items: []
  });
  const [newFoodItem, setNewFoodItem] = useState({
    name: '',
    contains_allergens: []
  });

  useEffect(() => {
    fetchFestivalData();
  }, [festivalId]);

  const fetchFestivalData = async () => {
    try {
      const [festivalRes, wristbandsRes, vendorsRes] = await Promise.all([
        axios.get(`${API}/festival/${festivalId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/festival/${festivalId}/wristbands`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/festival/${festivalId}/vendors`)
      ]);
      setFestival(festivalRes.data);
      setWristbands(wristbandsRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      console.error('Failed to fetch festival data:', error);
      toast.error('Failed to load festival data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAllergen = (allergen) => {
    setNewWristband(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergen)
        ? prev.allergens.filter(a => a !== allergen)
        : [...prev.allergens, allergen]
    }));
  };

  const addCustomAllergen = () => {
    if (customAllergen.trim() && !newWristband.custom_allergens.includes(customAllergen.trim())) {
      setNewWristband(prev => ({
        ...prev,
        custom_allergens: [...prev.custom_allergens, customAllergen.trim()]
      }));
      setCustomAllergen('');
    }
  };

  const removeCustomAllergen = (allergen) => {
    setNewWristband(prev => ({
      ...prev,
      custom_allergens: prev.custom_allergens.filter(a => a !== allergen)
    }));
  };

  const handleCreateWristband = async (e) => {
    e.preventDefault();
    if (newWristband.allergens.length === 0 && newWristband.custom_allergens.length === 0) {
      toast.error('Please select at least one allergen');
      return;
    }
    
    setCreatingWristband(true);
    try {
      const response = await axios.post(`${API}/festival/${festivalId}/wristband`, newWristband, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Wristband created successfully!');
      setWristbands([response.data, ...wristbands]);
      setWristbandDialogOpen(false);
      setNewWristband({ attendee_name: '', attendee_email: '', allergens: [], custom_allergens: [], notes: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create wristband');
    } finally {
      setCreatingWristband(false);
    }
  };

  const toggleFoodAllergen = (allergen) => {
    setNewFoodItem(prev => ({
      ...prev,
      contains_allergens: prev.contains_allergens.includes(allergen)
        ? prev.contains_allergens.filter(a => a !== allergen)
        : [...prev.contains_allergens, allergen]
    }));
  };

  const addFoodItem = () => {
    if (newFoodItem.name.trim()) {
      setNewVendor(prev => ({
        ...prev,
        food_items: [...prev.food_items, { ...newFoodItem }]
      }));
      setNewFoodItem({ name: '', contains_allergens: [] });
    }
  };

  const removeFoodItem = (index) => {
    setNewVendor(prev => ({
      ...prev,
      food_items: prev.food_items.filter((_, i) => i !== index)
    }));
  };

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    if (newVendor.food_items.length === 0) {
      toast.error('Please add at least one food item');
      return;
    }
    
    setCreatingVendor(true);
    try {
      const response = await axios.post(`${API}/festival/${festivalId}/vendor`, newVendor, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vendor added successfully!');
      setVendors([response.data, ...vendors]);
      setVendorDialogOpen(false);
      setNewVendor({ name: '', description: '', food_items: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vendor');
    } finally {
      setCreatingVendor(false);
    }
  };

  const copyAccessCode = () => {
    navigator.clipboard.writeText(festival?.access_code || '');
    toast.success('Access code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!festival) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Festival Not Found</h1>
          <Link to="/festival">
            <Button>Back to Festivals</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="festival-manage">
      <Navigation />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link 
          to="/festival" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Festivals
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{festival.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {festival.venue}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {festival.start_date} - {festival.end_date}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
              <span className="text-sm text-muted-foreground">Access Code:</span>
              <Badge variant="secondary" className="font-mono text-lg">{festival.access_code}</Badge>
              <Button variant="ghost" size="icon" onClick={copyAccessCode}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border border-border">
            <CardContent className="pt-6 text-center">
              <Users className="w-8 h-8 mx-auto mb-2 text-[#5DBDB3]" />
              <p className="text-2xl font-bold">{wristbands.length}</p>
              <p className="text-sm text-muted-foreground">Attendees</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="pt-6 text-center">
              <Store className="w-8 h-8 mx-auto mb-2 text-[#F08B7D]" />
              <p className="text-2xl font-bold">{vendors.length}</p>
              <p className="text-sm text-muted-foreground">Vendors</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="pt-6 text-center">
              <Palette className="w-8 h-8 mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold">{Object.keys(festival.allergen_colors || {}).length}</p>
              <p className="text-sm text-muted-foreground">Allergen Colors</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="pt-6 text-center">
              <QrCode className="w-8 h-8 mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold">{wristbands.length}</p>
              <p className="text-sm text-muted-foreground">QR Codes</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="wristbands" className="space-y-6">
          <TabsList>
            <TabsTrigger value="wristbands" data-testid="tab-wristbands">
              <Users className="w-4 h-4 mr-2" />
              Wristbands
            </TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors">
              <Store className="w-4 h-4 mr-2" />
              Vendors
            </TabsTrigger>
            <TabsTrigger value="colors" data-testid="tab-colors">
              <Palette className="w-4 h-4 mr-2" />
              Color Legend
            </TabsTrigger>
          </TabsList>

          {/* Wristbands Tab */}
          <TabsContent value="wristbands">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Registered Attendees</h2>
              <Dialog open={wristbandDialogOpen} onOpenChange={setWristbandDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#5DBDB3] hover:bg-[#4DA9A0]" data-testid="add-wristband-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Attendee
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Register Attendee</DialogTitle>
                    <DialogDescription>
                      Add an attendee and select their allergens
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateWristband} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="attendee_name">Name *</Label>
                        <Input
                          id="attendee_name"
                          placeholder="John Doe"
                          value={newWristband.attendee_name}
                          onChange={(e) => setNewWristband({ ...newWristband, attendee_name: e.target.value })}
                          required
                          data-testid="attendee-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="attendee_email">Email</Label>
                        <Input
                          id="attendee_email"
                          type="email"
                          placeholder="john@example.com"
                          value={newWristband.attendee_email}
                          onChange={(e) => setNewWristband({ ...newWristband, attendee_email: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Main 14 Allergens</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {MAIN_ALLERGENS.map((allergen) => (
                          <div key={allergen} className="flex items-center space-x-2">
                            <Checkbox
                              id={allergen}
                              checked={newWristband.allergens.includes(allergen)}
                              onCheckedChange={() => toggleAllergen(allergen)}
                            />
                            <label htmlFor={allergen} className="text-sm cursor-pointer">
                              {allergen}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Custom/Unknown Allergens</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., Kiwi, Latex, Banana"
                          value={customAllergen}
                          onChange={(e) => setCustomAllergen(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomAllergen())}
                        />
                        <Button type="button" variant="outline" onClick={addCustomAllergen}>
                          Add
                        </Button>
                      </div>
                      {newWristband.custom_allergens.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {newWristband.custom_allergens.map((allergen) => (
                            <Badge key={allergen} variant="secondary" className="gap-1">
                              {allergen}
                              <button type="button" onClick={() => removeCustomAllergen(allergen)}>
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any additional notes..."
                        value={newWristband.notes}
                        onChange={(e) => setNewWristband({ ...newWristband, notes: e.target.value })}
                      />
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setWristbandDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        className="bg-[#5DBDB3] hover:bg-[#4DA9A0]"
                        disabled={creatingWristband}
                        data-testid="create-wristband-btn"
                      >
                        {creatingWristband ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          'Create Wristband'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {wristbands.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {wristbands.map((wristband) => (
                  <Card key={wristband.id} className="border border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{wristband.attendee_name}</h3>
                          {wristband.attendee_email && (
                            <p className="text-xs text-muted-foreground">{wristband.attendee_email}</p>
                          )}
                        </div>
                        <QrCode className="w-5 h-5 text-muted-foreground" />
                      </div>
                      
                      <div className="flex flex-wrap gap-1 mb-3">
                        {wristband.colors.map((color, index) => (
                          <div
                            key={index}
                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                            style={{ backgroundColor: color.color }}
                            title={color.allergen}
                          />
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap gap-1">
                        {[...wristband.allergens, ...wristband.custom_allergens].map((allergen) => (
                          <Badge key={allergen} variant="outline" className="text-xs">
                            {allergen}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border border-dashed border-border">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">No attendees registered</h3>
                  <p className="text-muted-foreground text-sm">Add attendees to create wristbands</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Food Vendors</h2>
              <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#F08B7D] hover:bg-[#E07A6C]" data-testid="add-vendor-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add Food Vendor</DialogTitle>
                    <DialogDescription>
                      Add vendor details and their food items with allergens
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateVendor} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="vendor_name">Vendor Name *</Label>
                      <Input
                        id="vendor_name"
                        placeholder="Joe's Burgers"
                        value={newVendor.name}
                        onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                        required
                        data-testid="vendor-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vendor_desc">Description</Label>
                      <Input
                        id="vendor_desc"
                        placeholder="Gourmet burgers and fries"
                        value={newVendor.description}
                        onChange={(e) => setNewVendor({ ...newVendor, description: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-4 border-t pt-4">
                      <Label>Food Items</Label>
                      
                      {/* Add food item form */}
                      <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                        <Input
                          placeholder="Food item name (e.g., Cheeseburger)"
                          value={newFoodItem.name}
                          onChange={(e) => setNewFoodItem({ ...newFoodItem, name: e.target.value })}
                        />
                        <div className="grid grid-cols-3 gap-2">
                          {MAIN_ALLERGENS.map((allergen) => (
                            <div key={allergen} className="flex items-center space-x-2">
                              <Checkbox
                                id={`food-${allergen}`}
                                checked={newFoodItem.contains_allergens.includes(allergen)}
                                onCheckedChange={() => toggleFoodAllergen(allergen)}
                              />
                              <label htmlFor={`food-${allergen}`} className="text-xs cursor-pointer">
                                {allergen}
                              </label>
                            </div>
                          ))}
                        </div>
                        <Button type="button" variant="outline" onClick={addFoodItem} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Add Item
                        </Button>
                      </div>
                      
                      {/* Added food items */}
                      {newVendor.food_items.length > 0 && (
                        <div className="space-y-2">
                          {newVendor.food_items.map((item, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                              <div>
                                <p className="font-medium text-sm">{item.name}</p>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {item.contains_allergens.map((a) => (
                                    <Badge key={a} variant="outline" className="text-xs bg-[#F08B7D]/10 text-[#F08B7D]">
                                      {a}
                                    </Badge>
                                  ))}
                                  {item.contains_allergens.length === 0 && (
                                    <Badge variant="outline" className="text-xs bg-[#5DBDB3]/10 text-[#5DBDB3]">
                                      No major allergens
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Button type="button" variant="ghost" size="icon" onClick={() => removeFoodItem(index)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setVendorDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        className="bg-[#F08B7D] hover:bg-[#E07A6C]"
                        disabled={creatingVendor}
                        data-testid="create-vendor-btn"
                      >
                        {creatingVendor ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : (
                          'Add Vendor'
                        )}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {vendors.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {vendors.map((vendor) => (
                  <Card key={vendor.id} className="border border-border">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{vendor.name}</CardTitle>
                          {vendor.description && (
                            <CardDescription>{vendor.description}</CardDescription>
                          )}
                        </div>
                        <Link to={`/vendor/${vendor.id}/display`} target="_blank">
                          <Button variant="outline" size="sm">
                            <Printer className="w-4 h-4 mr-1" />
                            Display
                          </Button>
                        </Link>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Safe colors */}
                      <div>
                        <p className="text-sm font-medium text-[#5DBDB3] mb-2 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          Safe to eat ({vendor.safe_for_colors?.length || 0} colors)
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {vendor.safe_for_colors?.slice(0, 10).map((color, index) => (
                            <div
                              key={index}
                              className="w-6 h-6 rounded-full border border-border"
                              style={{ backgroundColor: color.color }}
                              title={color.allergen}
                            />
                          ))}
                          {vendor.safe_for_colors?.length > 10 && (
                            <span className="text-xs text-muted-foreground">+{vendor.safe_for_colors.length - 10} more</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Warning colors */}
                      {vendor.warning_colors?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-[#F08B7D] mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Cannot eat ({vendor.warning_colors.length} colors)
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {vendor.warning_colors.map((color, index) => (
                              <div
                                key={index}
                                className="w-6 h-6 rounded-full border-2 border-[#F08B7D]"
                                style={{ backgroundColor: color.color }}
                                title={color.allergen}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Food items */}
                      <div className="pt-2 border-t">
                        <p className="text-sm font-medium mb-2">Menu Items:</p>
                        <div className="space-y-1">
                          {vendor.food_items?.map((item, index) => (
                            <div key={index} className="text-sm text-muted-foreground">
                              • {item.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border border-dashed border-border">
                <CardContent className="py-12 text-center">
                  <Store className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">No vendors added</h3>
                  <p className="text-muted-foreground text-sm">Add food vendors to set up their display boards</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Color Legend Tab */}
          <TabsContent value="colors">
            <Card className="border border-border">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Allergen Color Legend</CardTitle>
                    <CardDescription>Print this for your festival entrance</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => window.print()}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print Legend
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" id="color-legend">
                  {Object.entries(festival.allergen_colors || {}).map(([allergen, colorInfo]) => (
                    <div key={allergen} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div
                        className="w-10 h-10 rounded-full border-2 border-white shadow-md flex-shrink-0"
                        style={{ backgroundColor: colorInfo.color }}
                      />
                      <div>
                        <p className="font-medium text-sm capitalize">{allergen}</p>
                        <p className="text-xs text-muted-foreground">{colorInfo.name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default FestivalManage;
